import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, XIcon, StarIcon, ArrowRightIcon } from 'lucide-react';
export function BenefitsSection() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  const achievements = ['entender cómo funciona un mini-negocio visual con IA,', 'ver una oportunidad actual con ojos más estratégicos,', 'estructurar una oferta más clara y más vendible,', 'simplificar el proceso de creación,', 'usar tu celular como herramienta de ejecución,', 'avanzar sin sentirte perdida ni abrumada,', 'y dar un paso real hacia una nueva fuente de ingresos.'];
  const forWho = ['estás cansada de depender solo de tu sueldo,', 'quieres empezar algo propio sin complicarte,', 'tienes poco tiempo, pero muchas ganas de cambiar tu situación,', 'quieres una opción digital más simple y actual,', 'no quieres lidiar con productos físicos, inventario ni logística,', 'quieres usar tu celular como una herramienta de ingresos,', 'y te atrae la idea de emprender con una habilidad moderna y práctica.'];
  const notForWho = ['buscas dinero fácil sin hacer trabajo real,', 'no estás dispuesta a aprender algo nuevo,', 'quieres resultados sin implementar,', 'o prefieres seguir observando desde fuera en lugar de dar el primer paso.'];
  return <section className="bg-cream py-20 md:py-32" data-id="element-2">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-3">
        {/* What you will achieve */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-slate-100 mb-16" data-id="element-4">
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-navy mb-8 text-center" data-id="element-5">
            Lo que vas a lograr dentro de{' '}
            <span className="text-gold" data-id="element-6">IA Express 2026</span>
          </motion.h2>

          <motion.p variants={itemVariants} className="text-lg text-slate-600 mb-8 text-center max-w-2xl mx-auto" data-id="element-7">
            Con esta guía vas a poder:
          </motion.p>

          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto" data-id="element-8">
            {achievements.map((item, index) => <motion.div key={index} variants={itemVariants} className="flex items-start gap-3 bg-cream/50 p-4 rounded-xl" data-id="element-9">
                <StarIcon className="w-5 h-5 text-gold shrink-0 mt-1 fill-gold/20" data-id="element-10" />
                <span className="text-slate-700" data-id="element-11">{item}</span>
              </motion.div>)}
          </div>

          <motion.p variants={itemVariants} className="text-xl font-serif font-medium text-navy text-center mt-10" data-id="element-12">
            No se trata solo de aprender. Se trata de empezar a construir.
          </motion.p>
        </motion.div>

        {/* Who is it for / Not for */}
        <div className="grid md:grid-cols-2 gap-8 mb-20" data-id="element-13">
          {/* For Who */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={containerVariants} className="bg-white rounded-3xl p-8 shadow-sm border-t-4 border-t-emerald-500" data-id="element-14">
            <motion.h3 variants={itemVariants} className="text-2xl font-bold text-navy mb-6" data-id="element-15">
              ¿Para quién es IA Express 2026?
            </motion.h3>
            <motion.p variants={itemVariants} className="text-slate-600 mb-6" data-id="element-16">
              Este programa es ideal para ti si:
            </motion.p>
            <div className="space-y-4" data-id="element-17">
              {forWho.map((item, index) => <motion.div key={index} variants={itemVariants} className="flex items-start gap-3" data-id="element-18">
                  <CheckIcon className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" data-id="element-19" />
                  <span className="text-slate-700" data-id="element-20">{item}</span>
                </motion.div>)}
            </div>
          </motion.div>

          {/* Not For Who */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={containerVariants} className="bg-white rounded-3xl p-8 shadow-sm border-t-4 border-t-rose-500" data-id="element-21">
            <motion.h3 variants={itemVariants} className="text-2xl font-bold text-navy mb-6" data-id="element-22">
              ¿Para quién NO es?
            </motion.h3>
            <motion.p variants={itemVariants} className="text-slate-600 mb-6" data-id="element-23">
              IA Express 2026 no es para ti si:
            </motion.p>
            <div className="space-y-4 mb-8" data-id="element-24">
              {notForWho.map((item, index) => <motion.div key={index} variants={itemVariants} className="flex items-start gap-3" data-id="element-25">
                  <XIcon className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" data-id="element-26" />
                  <span className="text-slate-700" data-id="element-27">{item}</span>
                </motion.div>)}
            </div>
            <motion.div variants={itemVariants} className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-28">
              <p className="text-sm text-slate-600 italic" data-id="element-29">
                Este programa es para personas que quieren avanzar con una ruta
                clara y con disposición real de actuar.
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* How it works */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={containerVariants} className="text-center max-w-3xl mx-auto" data-id="element-30">
          <motion.h2 variants={itemVariants} className="text-3xl md:text-4xl font-bold text-navy mb-6" data-id="element-31">
            Cómo funciona
          </motion.h2>
          <motion.p variants={itemVariants} className="text-lg text-slate-600 mb-12" data-id="element-32">
            El proceso dentro de IA Express 2026 está diseñado para ayudarte a
            avanzar con simplicidad.
          </motion.p>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-2 mb-12" data-id="element-33">
            {['Entender', 'Estructurar', 'Organizar', 'Lanzar'].map((step, index) => <motion.div key={index} variants={itemVariants} className="flex flex-col md:flex-row items-center gap-4 md:gap-2 w-full md:w-auto" data-id="element-34">
                  <div className="bg-navy text-white px-6 py-3 rounded-full font-medium shadow-md w-full md:w-auto" data-id="element-35">
                    {step}
                  </div>
                  {index < 3 && <ArrowRightIcon className="w-6 h-6 text-gold hidden md:block" data-id="element-36" />}
                  {index < 3 && <ArrowRightIcon className="w-6 h-6 text-gold rotate-90 md:hidden" data-id="element-37" />}
                </motion.div>)}
          </div>

          <div className="space-y-6 text-lg text-slate-700 leading-relaxed text-left md:text-center bg-white p-8 rounded-2xl shadow-sm" data-id="element-38">
            <motion.p variants={itemVariants} data-id="element-39">
              Primero, vas a entender el modelo y la oportunidad. Después, vas a
              ver cómo estructurar una propuesta visual clara. Luego, vas a
              organizar tu mini-negocio de forma práctica. Y finalmente, vas a
              quedar con una base mucho más sólida para salir a ofrecer tu
              servicio.
            </motion.p>
            <motion.p variants={itemVariants} className="font-medium text-navy" data-id="element-40">
              La idea no es saturarte. La idea es ayudarte a moverte.
            </motion.p>
            <motion.div variants={itemVariants} className="bg-gold/10 border border-gold/30 p-6 rounded-xl mt-6" data-id="element-41">
              <p className="text-navy font-semibold mb-2" data-id="element-42">
                Y por eso todo está pensado para que puedas lanzar tu
                mini-negocio visual desde tu celular en{' '}
                <span className="text-gold text-2xl font-bold" data-id="element-43">48 horas</span>.
              </p>
              <p className="text-sm text-slate-600" data-id="element-44">
                No significa que en 48 horas ya tengas todo resuelto. Significa
                que en 48 horas puedes pasar de estar confundida… a tener una
                base clara, accionable y lista para empezar.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}